export const HELP_CONTENT = {
  affiliates: [
    {
      title: "Welcome to the Plaintr Creator Network",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>We’re excited to have you here.</p>
            <p>
              Plaintr was built to make taxes feel human — and our Creator
              Network was built to make partnership feel the same way.
            </p>
          </div>
          <div className="general-accordion-content-block">
            <p>
              Inside your dashboard, you’ll find everything you need to start
              sharing Plaintr confidently:
            </p>
            <ul>
              <li>Your personalized creator link</li>
              <li>Performance and payout tracking</li>
              <li>Learning resources and tutorials</li>
            </ul>
            <p>
              If you’re new, start here. This section walks you through the
              basics.
            </p>
          </div>
        </>
      ),
    },
    {
      title: "How the Program Works",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>
              Plaintr connects people who need trusted tax help with certified
              professionals — CPAs, EAs, and Tax Attorneys.
            </p>
            <p> As a creator, you share that clarity with your audience.</p>
            <p>
              When someone uses your link and completes a filing, you earn a
              reward — transparent, automatic, and trackable from your
              dashboard.
            </p>
            <h4>In short:</h4>
            <p>
              <em>
                You share clarity → your audience files smarter → you get
                rewarded.
              </em>
            </p>
            <p>
              Everything runs through Stripe for payments and 1099s, so you
              don’t have to handle anything manually.
            </p>
          </div>
        </>
      ),
    },
    {
      title: "Eligibility & Approval",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>To join, you must:</p>
            <ul>
              <li>Be 18 or older and reside in the U.S.</li>
              <li>Have an audience primarily based in the U.S.</li>
              <li>
                Create content that speaks about money, side hustles, or
                everyday financial life.
              </li>
              <li>
                Follow FTC disclosure guidelines and our Affiliate Agreement.
              </li>
            </ul>
            <p>
              Once your application is approved, you’ll receive a confirmation
              email and gain access to your dashboard.
            </p>
            <p>From there, you can start generating links immediately.</p>
          </div>
        </>
      ),
    },
    {
      title: "How to Generate and Share Your Link",
      content: (
        <>
          <div className="general-accordion-content-block">
            <ul>
              <li>Go to Links in your dashboard.</li>
              <li>Click Generate my link.</li>
              <li>
                Copy it and share it with your audience — in your bio, caption,
                or story.
              </li>
              <li>
                Always include your disclosure:{" "}
                <em>
                  “I partner with Plaintr to help people file smarter.
                  #PlaintrPartner”
                </em>
              </li>
            </ul>
            <p>
              Each completed filing through your link counts as a tracked
              conversion. You’ll see performance updates in real time.
            </p>
          </div>
        </>
      ),
    },
    {
      title: "Payouts & Timeline",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>Plaintr uses Stripe Connect to manage payouts securely.</p>
            <ul>
              <li>
                Payouts are processed monthly once conversions are verified.
              </li>
              <li>You’ll receive an email when your payout is ready.</li>
              <li>
                Stripe automatically handles your W-9 and issues 1099-NEC forms
                if applicable.
              </li>
            </ul>
          </div>
        </>
      ),
    },
    {
      title: "Support & Resources",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>Need help or have questions? You have two support options:</p>
            <ul>
              <li>
                <strong>Help Center:</strong> Browse tutorials and FAQs.
              </li>
              <li>
                <strong>Email Support:</strong> support@plaintr.com.
              </li>
            </ul>
          </div>
        </>
      ),
    },
    {
      title: "Common Questions",
      content: (
        <>
          <div className="general-accordion-content-block">
            <div>
              <h4>Q: When will I get paid?</h4>
              <p>
                Once a referred client completes a filing, your payout is queued
                for the next payout cycle. Stripe will notify you automatically.
              </p>
            </div>
            <div>
              <h4>Q: Can I use my link in paid ads?</h4>
              <p>
                No. For compliance reasons, Plaintr links can only be used in
                organic content (posts, videos, stories, blogs).
              </p>
            </div>
            <div>
              <h4>Q: What if a user cancels or requests a refund?</h4>
              <p>
                If a refund is issued, the conversion is reversed. You’ll see
                this reflected in your performance tab.
              </p>
            </div>
            <div>
              <h4>Q: Can I partner with other financial brands too?</h4>
              <p>
                Yes, as long as your content doesn’t misrepresent or conflict
                with Plaintr’s services. Always disclose partnerships clearly.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Quick Checklist Before You Start",
      content: (
        <>
          <div className="general-accordion-content-block">
            <ul>
              <li>You’ve read and accepted the Affiliate Agreement.</li>
              <li>Your Stripe account is connected.</li>
              <li>You’ve reviewed the Social Media Do’s & Don’ts.</li>
              <li>You’ve set up your first creator link.</li>
              <li>
                You’re ready to share your first post with proper disclosure.
              </li>
            </ul>
          </div>
        </>
      ),
    },
  ],
  providers: [
    {
      title: "What it means to join the Plaintr marketplace",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>
              Learn about joining Plaintr’s exclusive marketplace for certified
              CPAs, EAs, and Tax Attorneys.
            </p>
            <h4>A curated marketplace for tax pros</h4>
            <p>
              Plaintr is built exclusively for CPAs, EAs, and Tax Attorneys. By
              joining Plaintr, you become part of a premium network of certified
              professionals who are matched with clients based on specialization
              and expertise.
            </p>
            <p>
              This isn’t gig work. This is about being matched with the right
              cases and working with clients who value your expertise.
            </p>
          </div>
          <div className="general-accordion-content-block">
            <h4>Why Plaintr is different</h4>
            <ul>
              <li>
                <strong>Exclusivity:</strong> Plaintr only works with licensed
                professionals who meet our standards.
              </li>
              <li>
                <strong>Control:</strong> You choose which cases you want, based
                on your expertise and capacity.
              </li>
              <li>
                <strong>Transparent pricing:</strong> You know the fee before
                you accept any cases, and your payment is securely held until
                the work is done.
              </li>
            </ul>
          </div>
          <div className="general-accordion-content-block">
            <h4>How it works for you</h4>
            <ul>
              <li>
                <strong>Smart Match:</strong> Get matched with clients based on
                your areas of specialization.
              </li>
              <li>
                <strong>Fair pricing:</strong> No surprise rates — you and the
                client agree to a price upfront.
              </li>
              <li>
                <strong>Payment protection:</strong> Your payment is held
                securely until you finish the work.
              </li>
            </ul>
          </div>
          <div className="general-accordion-content-block">
            <h4>Why it matters</h4>
            <p>
              When you join Plaintr, you’re joining a premium, curated
              marketplace that values the integrity of your work. There’s no
              micromanaging, no quotas — just clients who need your specialized
              expertise and are willing to pay a fair price for it.
            </p>
          </div>
        </>
      ),
    },
    {
      title: "How Smart Match works for Pros",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>
              <em>
                Learn how Plaintr’s Smart Match system connects you with the
                right clients based on your expertise and availability.
              </em>
            </p>
            <h4>Matching you with the right clients</h4>
            <p>
              Smart Match is Plaintr’s system that connects you with clients
              based on:
            </p>
            <ul>
              <li>
                Your areas of expertise (business tax, crypto, multi-state,
                etc.)
              </li>
              <li>Your availability</li>
              <li>The client’s tax situation</li>
            </ul>
          </div>
          <div className="general-accordion-content-block">
            <h4>How it works</h4>
            <ul>
              <li>
                <strong>Create your pro profile:</strong> Tell us about your
                credentials, specialties, and availability.
              </li>
              <li>
                <strong>Get matched:</strong> Smart Match finds clients who need
                your expertise.
              </li>
              <li>
                <strong>Accept or decline:</strong> You decide whether to accept
                the case or not. If the case aligns with your expertise and
                schedule, you accept. If not, you decline, and the system finds
                another pro.
              </li>
            </ul>
          </div>
          <div className="general-accordion-content-block">
            <h4>Why it’s different</h4>
            <p>
              Plaintr isn’t about assigning cases randomly or forcing you into
              high-volume, low-margin work. You’re always in control of which
              clients you choose.
            </p>
          </div>
        </>
      ),
    },
    {
      title: "Case control for tax pros",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>
              <em>
                Learn how you can accept or decline cases based on your
                availability and expertise.
              </em>
            </p>
            <h4>Full control over your workload</h4>
            <p>
              At Plaintr, you’re in control of your schedule. Once Smart Match
              matches you with a client, you can choose whether to accept or
              decline the case. If you decline, the system will find another pro
              with the right expertise.
            </p>
            <p>
              This means you’re not pressured into taking on cases you’re not
              comfortable with. You can focus on the work that fits your
              expertise and schedule.
            </p>
          </div>
          <div className="general-accordion-content-block">
            <h4>Why this matters</h4>
            <ul>
              <li>
                <strong>Control:</strong> You choose the cases you want to take,
                based on your expertise and availability.
              </li>
              <li>
                <strong>Fairness:</strong> No random assignments. You’re always
                matched with cases that align with your skills.
              </li>
              <li>
                <strong>Respect:</strong> Your time and expertise are respected.
              </li>
            </ul>
          </div>
        </>
      ),
    },
    {
      title: "Upfront Pricing & Protected Funds: How Payments Work for Pros",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>
              <em>
                Learn about upfront pricing, a secure payment hold, and how
                you’ll get paid for the work you do on Plaintr.
              </em>
            </p>
            <h4>Upfront pricing for peace of mind</h4>
            <p>
              Plaintr uses upfront pricing, so you know what you’ll earn before
              you accept a case. The client agrees to a price before work
              begins, and the funds are securely held until the work is done.
            </p>
          </div>
          <div className="general-accordion-content-block">
            <h4>How payments work</h4>
            <p>
              <strong>Upfront fees, no surprises:</strong> Every case has a
              flat, upfront fee agreed before work begins. You know exactly what
              you’ll earn before you accept the case.
            </p>
            <p>
              <strong>Secure payment hold:</strong>
            </p>
            <ul>
              <li>Client pays upfront</li>
              <li>Funds are securely held</li>
              <li>Payment is released once the work is complete.</li>
            </ul>
          </div>
          <div className="general-accordion-content-block">
            <h4>Why this matters</h4>
            <p>
              This system removes the worry about late payments, underpricing,
              or surprise fees. You get paid fairly for your work — and your
              payment is protected until you complete the job.
            </p>
          </div>
        </>
      ),
    },
    {
      title: "Disputes from the Pro perspective",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>
              <em>
                Learn how Plaintr handles disputes from the Pro side, ensuring
                fairness and respect for tax professionals during conflict
                resolution.
              </em>
            </p>
            <h4>How we handle disputes</h4>
            <p>
              At Plaintr, we believe in transparency and fairness. If a client
              or a pro has an issue, we make sure there’s a clear path to
              resolution:
            </p>
            <u>
              <li>
                <strong>Start with a conversation:</strong> Plaintr gives both
                parties a chance to work things out directly through informal
                negotiations.
              </li>
              <li>
                <strong>Internal Resolution Panel (IRP):</strong> If that
                doesn’t work, we escalate it to Plaintr’s IRP, which is made up
                of a compliance officer and a senior CPA/EA. The IRP will review
                the details and issue a decision within 10 business days.
              </li>
              <li>
                <strong>Escalation to Arbitration:</strong> If a decision isn’t
                satisfactory, the dispute can be escalated to AAA arbitration
                for a final decision.
              </li>
            </u>
          </div>
          <div className="general-accordion-content-block">
            <h4>How it works</h4>
            <ul>
              <li>
                <strong>Dispute submission:</strong> File a dispute easily
                through the Plaintr dashboard or by emailing
                support@plaintr.com.
              </li>
              <li>
                <strong>Evidence submission:</strong> Both the pro and client
                submit supporting evidence to help the IRP make a fair decision.
              </li>
              <li>
                <strong>Resolution outcomes:</strong> The IRP can decide to
                issue a full refund, a partial refund, corrective work, or
                reassign the case to another pro.
              </li>
              <li>
                <strong>Appeals:</strong> If new evidence is presented, either
                party can file an appeal. But once the arbitration decision is
                made, it’s final.
              </li>
            </ul>
          </div>
          <div className="general-accordion-content-block">
            <h4>Why this matters</h4>
            <p>
              We want to ensure that you’re protected. At Plaintr, we don’t
              leave you in the dark about disputes. The process is transparent,
              the outcome fair, and payments remain under a secure payment hold
              until everything is settled.
            </p>
          </div>
        </>
      ),
    },
    {
      title: "Service Agreement (summary in plain English for Pros)",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>
              <em>Learn your rights and responsibilities in simple terms.</em>
            </p>
            <h4>What you need to know</h4>
            <p>
              The Service Agreement sets out how we work together to make sure
              everything runs smoothly, fairly, and professionally. Here’s a
              breakdown of what’s covered:
            </p>
            <div>
              <h5 className="accordion-hfive">Confidentiality</h5>
              <p>
                Your clients trust you with sensitive data, and Plaintr has your
                back in protecting that. We ensure client information stays
                private — and it’s up to both of us to handle it securely.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Payments</h5>
              <ul>
                <li>
                  <strong>Upfront Pricing:</strong> Plaintr works with flat,
                  upfront fees for all cases. You agree on your fee before
                  accepting a case, so there are no surprises.
                </li>
                <li>
                  <strong>Secure payment hold:</strong> When the client pays,
                  their payment is placed under a secure payment hold until you
                  complete the work.This guarantees you’ll be paid fairly for
                  your expertise once the job’s done.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Timeframes</h5>
              <ul>
                <li>
                  <strong>Fast responses:</strong> Clients expect a response
                  within 1 business hour for new cases, and 12 business hours
                  for ongoing engagements.
                </li>
                <li>
                  <strong>Proactive updates:</strong> If there’s no activity
                  from the client, you’ll need to provide a quick update every 2
                  business days to keep them in the loop.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Dispute Resolution</h5>
              <ul>
                <li>
                  <strong>Internal Resolution Panel (IRP):</strong> If a dispute
                  arises, we’ll work with you to solve it fairly. The IRP (a
                  3-person team, including a compliance officer and a senior
                  CPA/EA) will review the case.
                </li>
                <li>
                  <strong>Quick resolution:</strong> The IRP aims to resolve
                  disputes within 10 business days (and within 3 business days
                  for urgent cases).
                </li>
                <li>
                  <strong>Escalation to Arbitration:</strong> If needed, we can
                  take the issue to binding arbitration with the American
                  Arbitration Association (AAA) — a fair and final decision
                  process.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Professional Standards</h5>
              <p>
                As a certified CPA, EA, or Tax Attorney, we know you take your
                ethical standards seriously. Plaintr trusts that:
              </p>
              <ul>
                <li>You’ll maintain honesty and integrity in your work.</li>
                <li>
                  You’ll address any issues (such as errors or omissions)
                  promptly, ensuring clients receive the best service possible.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Termination and Reassignment</h5>
              <ul>
                <li>
                  <strong>Flexibility:</strong> You can end the agreement at any
                  time with 10 business days' notice. Plaintr can also terminate
                  the agreement for certain reasons, like breach of
                  confidentiality or performance issues.
                </li>
                <li>
                  <strong>Reassignment:</strong> If needed, we may reassign
                  clients to another pro. But we always aim for fairness and
                  transparency — and you can always decline to take on cases
                  that don’t fit your expertise.
                </li>
              </ul>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>Why it matters</h4>
            <p>
              This Service Agreement isn’t about rules — it’s about creating a
              seamless, fair, and empowering experience for you. Plaintr is here
              to support your expertise and make your job easier with clear
              expectations, easy payment, and protection for both you and the
              client.
            </p>
            <p>
              By joining Plaintr, you get the freedom to choose cases you’re
              best suited for, and the confidence that your payments and client
              relationships are protected. It’s all about respecting your time
              and talent, and ensuring you have a smooth path to success.
            </p>
          </div>
        </>
      ),
    },
  ],
  filings: [
    {
      title: "Getting Started",
      content: (
        <>
          <div className="general-accordion-content-block">
            <h4>What is Plaintr?</h4>
            <p>
              <em>
                Plaintr is a premium tax platform built for trust. Learn how we
                connect clients with certified CPAs, EAs, and Tax Attorneys
                through upfront pricing and a secure payment hold — designed to
                protect both clients and professionals.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Plaintr in one line</h5>
              <p>
                Plaintr is a tax platform that combines human expertise with
                smart, transparent systems — giving you access to CPAs, Enrolled
                Agents, and Tax Attorneys at a flat, upfront price.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">What Plaintr is (and isn’t)</h5>
              <ul>
                <li>
                  <strong>Not DIY software → </strong>You’re not left to figure
                  out the tax code alone.
                </li>
                <li>
                  <strong>Not a gig platform → </strong>We don’t use seasonal
                  hires or uncertified preparers.
                </li>
                <li>
                  <strong>Not corporate tax prep → </strong>We don’t push you
                  through office quotas or upsells.
                </li>
              </ul>
              <p>
                Plaintr is a curated network of certified experts, matched to
                your situation through our Smart Match system, with payments
                securely held until the work is complete.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Why Plaintr exists</h5>
              <p>Taxes are stressful for two reasons:</p>
              <ul>
                <li>
                  Uncertainty about cost — fees grow with every extra form or
                  “surprise” charge.
                </li>
                <li>
                  Uncertainty about quality — you never know who’s really
                  handling your return.
                </li>
              </ul>
              <p>
                Plaintr was built to solve both: flat, upfront pricing and
                credential-verified experts only.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">What you can expect</h5>
              <p>When you use Plaintr, you get:</p>
              <ul>
                <li>
                  <strong>Clarity → </strong>You know the cost before you begin.
                </li>
                <li>
                  <strong>Confidence → </strong>You’re working with a verified
                  CPA, EA, or Tax Attorney.
                </li>
                <li>
                  <strong>Control → </strong>You stay informed with updates and
                  full review before anything is filed.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">The bigger picture</h5>
              <p>
                Plaintr is more than a filing service. It’s a new way to think
                about taxes — human, smart, and fair.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>How Smart Match works — and what it really means</h4>
            <p>
              <em>
                Discover how Plaintr’s Smart Match connects you with the right
                tax professional — and why it’s different from DIY or gig
                platforms.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Step 1: Tell us your story</h5>
              <p>
                Your life doesn’t fit into a checkbox, and your taxes shouldn’t
                either. That’s why we start with the right questions — about
                your work, your family, your business, and your goals.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">
                Step 2: Get matched with the right pro
              </h5>
              <p>
                We hand-pick from our network of CPAs, Enrolled Agents, and Tax
                Attorneys. No seasonal hires. No uncertified preparers. Just
                credentialed professionals verified by Plaintr.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Step 3: Stay in control</h5>
              <p>Your pro handles the numbers, but you stay in charge:</p>
              <ul>
                <li>Direct messaging through the dashboard</li>
                <li>Status updates at every stage</li>
                <li>Full review before anything is filed</li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">What Smart Match really means</h5>
              <p>
                Smart Match isn’t just a matching tool. It’s our way of
                ensuring:
              </p>
              <ul>
                <li>You’re never assigned randomly.</li>
                <li>You’ll never work with someone unqualified.</li>
                <li>
                  You’ll always know the fee upfront — with your funds securely
                  held until the work is complete.
                </li>
              </ul>
              <p>
                In other words, Smart Match is not only about connecting you to
                a pro. It’s about making sure the right expert fits your
                reality, at a fair and transparent price.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>Who are the tax pros on Plaintr</h4>
            <p>
              <em>
                Learn why Plaintr works only with credentialed CPAs, Enrolled
                Agents, and Tax Attorneys — no seasonal hires, no uncertified
                preparers.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Certified experts only</h5>
              <p>
                Plaintr was built on a simple belief: if you’re trusting someone
                with your taxes, they should be fully certified and accountable.
                That’s why our network is limited to:
              </p>
              <ul>
                <li>Certified Public Accountants (CPAs)</li>
                <li>Enrolled Agents (EAs)</li>
                <li>Tax Attorneys</li>
              </ul>
              <p>No seasonal hires. No uncertified preparers. No exceptions.</p>
            </div>
            <div>
              <h5 className="accordion-hfive">How we verify credentials</h5>
              <p>
                Every professional on Plaintr goes through a strict onboarding
                process:
              </p>
              <ul>
                <li>
                  Compliance test based on IRS Circular 230 and Plaintr policies
                </li>
                <li>License verification against IRS and state boards</li>
                <li>Identity verification</li>
              </ul>
              <p>
                Only after passing this process can they accept cases on the
                platform.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Why this matters for you</h5>
              <ul>
                <li>
                  <strong>Credibility →</strong> You know you’re working with
                  someone recognized by the IRS or state bar.
                </li>
                <li>
                  <strong>Consistency →</strong> All Pros meet the same
                  standards, whether you’re filing as an individual, a business,
                  or with complex needs.
                </li>
                <li>
                  <strong>Confidence →</strong> You’re paying for expertise that
                  can stand behind its work — not for guesswork or seasonal
                  help.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">The Plaintr difference</h5>
              <p>
                Other platforms and chains may mix in seasonal staff or
                uncertified preparers for simpler returns. At Plaintr, every
                single case is handled by a licensed pro.
              </p>
              <p>
                That’s how we protect your trust — and how we make sure your
                taxes are done right the first time.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>How to create your Plaintr account and use your dashboard</h4>
            <p>
              <em>
                Learn how to create your Plaintr account, set up your profile,
                and use your dashboard to manage documents, payments, and
                messages with your pro.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Step 1: Create your account</h5>
              <p>Getting started takes just a few minutes.</p>
              <ul>
                <li>Go to plaintr.com and select Sign Up.</li>
                <li>Enter your name, email, and secure password.</li>
                <li>Confirm your email address to activate your account.</li>
              </ul>
              <p>That’s it — you’re ready to begin.</p>
            </div>
            <div>
              <h5 className="accordion-hfive">Step 2: Complete your profile</h5>
              <p>
                Your profile helps us understand your situation so Smart Match
                can connect you with the right pro. You’ll be asked about:
              </p>
              <ul>
                <li>Whether you’re filing as an individual or business</li>
                <li>
                  The types of income you receive (W-2, 1099, investments, etc.)
                </li>
                <li>
                  Any special circumstances (real estate, multiple states,
                  self-employment, etc.)
                </li>
              </ul>
              <p>
                This information stays private and secure — it’s only used to
                match you with a certified expert.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">
                Step 3: Explore your dashboard
              </h5>
              <p>
                Once your account is active, you’ll see your personalized
                dashboard. This is where you’ll:
              </p>
              <ul>
                <li>Upload documents (like W-2s, 1099s, prior returns)</li>
                <li>Message your pro directly and securely</li>
                <li>Track progress of your case from start to finish</li>
                <li>See payment status in real time</li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Step 4: Stay in control</h5>
              <p>
                The dashboard is designed so you’re never left guessing. You can
                always:
              </p>
              <ul>
                <li>Check what stage your filing is in</li>
                <li>Review drafts before anything is submitted</li>
                <li>
                  Access all communication and documents in one secure place
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">
                Your gateway to filing, Plaintr style
              </h5>
              <p>
                Your dashboard is more than just a portal — it’s the command
                center for your taxes. It keeps everything organized,
                transparent, and under your control.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>How Plaintr is Different</h4>
            <p>
              <em>
                Plaintr isn’t DIY or corporate. We built a human-first tax
                platform where certified experts handle your case with clarity,
                fairness, and trust.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Filing that feels human</h5>
              <p>
                The tax industry forgot who it was supposed to serve: people.
                Filing became about forms, quotas, and systems — not about you.
              </p>
              <p>
                Plaintr is different. We start with your life — your work, your
                family, your business — and connect you to a certified expert
                who understands how those details shape your taxes.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Not DIY. Not Corporate.</h5>
              <ul>
                <li>
                  <strong>Not DIY software → </strong>You’re not left alone with
                  confusing rules or risky guesswork.
                </li>
                <li>
                  <strong>Not corporate tax prep → </strong>You’re not shuffled
                  to whoever’s available in a retail office.
                </li>
                <li>
                  <strong>Always certified experts → </strong>Every case is
                  handled by a CPA, Enrolled Agent, or Tax Attorney, vetted and
                  verified before they ever take on a client.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Built for clarity and trust</h5>
              <ul>
                <li>
                  <strong>Upfront, flat pricing → </strong>No bait-and-switch
                  estimates. The price you see is the price you pay.
                </li>
                <li>
                  <strong>Secure payment hold → </strong>Payments stay protected
                  until the work is done.
                </li>
                <li>
                  <strong>Respect for both sides → </strong>Clients get clarity,
                  Pros get fairness. That’s how trust is built.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">The Plaintr Standard</h5>
              <p>Plaintr isn’t just a platform. It’s a higher standard:</p>
              <ul>
                <li>
                  Accessible expertise once reserved for high-net-worth clients.
                </li>
                <li>
                  Peace of mind that your case is handled with care, not quotas.
                </li>
                <li>
                  Belonging to a service where every pro is invested in your
                  best outcome.
                </li>
              </ul>
              <p>
                Your life. Your numbers. Your peace of mind. That’s Plaintr.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Payments & Pricing",
      content: (
        <>
          <div className="general-accordion-content-block">
            <h4>Why Plaintr uses upfront flat fees</h4>
            <p>
              <em>
                Learn why Plaintr uses a flat, upfront payment model and how it
                protects both clients and professionals.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Why we charge upfront</h5>
              <p>
                At Plaintr, we don’t believe in hidden upsells, surprise
                invoices, or bait-and-switch estimates. That’s why we use a flat
                fee, paid upfront.
              </p>
              <p>This approach is simple:</p>
              <ul>
                <li>You know the exact cost before work begins.</li>
                <li>Your payment is securely held until the job is done.</li>
                <li>
                  Your professional can focus on doing the work right — not
                  chasing payments.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Why it’s better for you</h5>
              <p>
                Working with a CPA, Enrolled Agent, or Tax Attorney often comes
                with an open-ended hourly rate or a bill that grows as your case
                gets more complex.
              </p>
              <p>
                Other platforms often estimate a low fee, then adjust once the
                work is complete. With Plaintr, what you see is what you pay.
              </p>
              <p>
                That means you get access to the same certified expertise —
                without surprises, without negotiation, unexpected costs or
                wasted time.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">
                Why it’s better for professionals
              </h5>
              <p>
                Our network is built for CPAs, Enrolled Agents, and Tax
                Attorneys only. These certified experts expect clarity and
                respect for their time. Upfront payment ensures they’re
                protected, so they can focus fully on your case — not on chasing
                down invoices.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>How Plaintr prices your case</h4>
            <p>
              <em>
                Learn how Plaintr sets your fee upfront through Smart Match,
                based on your real tax situation, so you get the right expert
                and no surprise bills.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">
                Pricing that starts with your life
              </h5>
              <p>
                At Plaintr, your price isn’t guessed, hidden, or left
                open-ended. It’s set through Smart Match — our intake system
                that decodes your tax situation to calculate a fair, upfront fee
                and connect you with the right certified professional.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">How Smart Match sets your fee</h5>
              <p>
                When you complete Smart Match, you’re not just filling out a
                form. You’re telling us your story:
              </p>
              <ul>
                <li>
                  Do you work for an employer, run your own business, or juggle
                  both?
                </li>
                <li>
                  Do you invest in stocks, trade crypto, or own real estate?
                </li>
                <li>
                  Do you file across multiple states or manage more complex
                  structures?
                </li>
              </ul>
              <p>
                From these details, we calculate your flat fee and match you
                with the exact type of expert your case needs: a CPA, an
                Enrolled Agent, or a Tax Attorney.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Locked in, from the start</h5>
              <p>
                Your fee is personalized to your situation and locked in before
                a single document changes hands. It will never change — unless
                key details are left out during Smart Match.
              </p>
              <p>This means:</p>
              <ul>
                <li>No hourly rates ticking in the background.</li>
                <li>No “starting at” estimates that double later.</li>
                <li>No surprise bills after the work is done.</li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Why it matters</h5>
              <p>
                Traditional pricing models reward confusion. The more complex
                your taxes get, the more you pay. Plaintr flips that:
              </p>
              <ul>
                <li>You know the number upfront.</li>
                <li>You know who you’re working with.</li>
                <li>You can focus on your life, not your bill.</li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">The Plaintr promise</h5>
              <p>
                Smart Match ensures you’re paired with the right expert for your
                case and given a price that’s fair for both sides. That’s how
                Plaintr makes certified expertise accessible — and how we
                replace uncertainty with peace of mind.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>Payment methods accepted on Plaintr</h4>
            <p>
              <em>
                Learn which payment methods Plaintr accepts and how your payment
                is kept safe until your tax work is complete.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Simple, secure payments</h5>
              <p>
                Plaintr accepts major credit and debit cards (Visa, Mastercard,
                AmEx, Discover). All payments are securely processed by Stripe —
                an industry-trusted payment provider used by companies
                worldwide.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Why no checks or cash?</h5>
              <p>
                Because Plaintr safely holds every payment until the work is
                complete, we only use digital payment methods that are instant
                and traceable. This protects you and your pro.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Your receipt, every time?</h5>
              <p>
                Every payment you make generates a digital receipt in your
                dashboard — so you always have a record for your files.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>Where your money goes on Plaintr</h4>
            <p>
              <em>
                Learn how Plaintr’s system protects your money by holding it
                safely until your tax work is completed.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Safe, not spent</h5>
              <p>
                When you pay on Plaintr, your payment doesn’t go straight to
                your tax professional. It is placed under a secure payment hold
                managed through Plaintr’s payment infrastructure.
              </p>
              <p>How it works</p>
              <ul>
                <li>
                  You pay upfront → Your fee is securely held once payment is
                  completed.
                </li>
                <li>
                  Your pro does the work → With clarity that payment is
                  protected and reserved.
                </li>
                <li>
                  Payment is released → Only after the work is completed and the
                  case is closed.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Why it matters</h5>
              <p>A secure payment hold protects both sides:</p>
              <ul>
                <li>
                  Clients → Peace of mind that no one gets paid until the work
                  is done.
                </li>
                <li>
                  Pros → Confidence that the funds are real and available once
                  they finish the job.
                </li>
              </ul>
              <p>It’s a fair system that replaces uncertainty with trust.</p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>How Plaintr is different from gig apps</h4>
            <p>
              <em>
                Unlike gig apps, Plaintr works exclusively with certified CPAs,
                EAs, and Tax Attorneys — and protects both clients and
                professionals through upfront pricing and a secure payment hold.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">
                Gig apps aren’t built for taxes
              </h5>
              <p>
                On gig platforms, anyone can call themselves a “tax pro.”
                Credentials are optional, prices are unpredictable, and the
                quality of work can vary wildly. That may be fine for food
                delivery — but not for your taxes.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Plaintr is built differently</h5>
              <ul>
                <li>
                  <strong>Certified experts only →</strong> Every case is
                  handled by a CPA, EA, or Tax Attorney. No exceptions.
                </li>
                <li>
                  <strong>Upfront, flat pricing → </strong>You know your
                  personalized fee before you begin.
                </li>
                <li>
                  <strong>Secure payment hold → </strong>Payments are held
                  securely until the work is complete.
                </li>
                <li>
                  <strong>Case choice for Pros → </strong>Experts choose which
                  cases they want, ensuring commitment and quality.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Why this matters for you</h5>
              <p>
                With Plaintr, you’re not rolling the dice on who you’ll get or
                how much you’ll end up paying. You’re choosing a platform built
                for trust, transparency, and professional standards.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Refunds & Disputes",
      content: (
        <>
          <div className="general-accordion-content-block">
            <h4>Am I eligible for a refund?</h4>
            <p>
              <em>
                Find out when refunds apply on Plaintr, what’s covered, and how
                requests are reviewed.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">When refunds apply</h5>
              <p>
                Refunds exist as a safeguard for rare situations outside your
                control. They’re not common — but if something unexpected
                happens, you’re covered.
              </p>
              <p>Examples include:</p>
              <ul>
                <li>
                  Work never begins after you’ve provided all required
                  information.
                </li>
                <li>
                  Deliverables are incomplete or fall below professional
                  standards.
                </li>
                <li>
                  A technical issue occurs in the matching process (extremely
                  rare, but protected if it does).
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">When refunds don’t apply</h5>
              <p>
                Refunds aren’t designed for situations of preference or
                convenience. For example:
              </p>
              <ul>
                <li>You change your mind after work has begun.</li>
                <li>
                  You don’t provide the required documents or information.
                </li>
                <li>
                  The work was completed as agreed, but you later decide not to
                  use it.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">How decisions are made</h5>
              <p>
                Refund requests are reviewed by our Internal Resolution Panel
                (IRP). They look at the facts, review evidence, and decide
                fairly. Funds remain on hold until the case is resolved.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Your protection</h5>
              <p>You’re never left in the dark. You’ll receive:</p>
              <ul>
                <li>A clear explanation of the decision</li>
                <li>Documentation of what was reviewed</li>
                <li>Confirmation of how funds will be released</li>
              </ul>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>How to request a refund (step by step)</h4>
            <p>
              <em>
                Follow these simple steps to request a refund on Plaintr. Learn
                how the process works and what to expect.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Step 1: Start with your pro</h5>
              <p>
                If something isn’t right, the fastest solution is often the
                simplest. First, message your pro directly through the
                dashboard. Many issues can be fixed quickly without needing a
                refund.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">
                Step 2: Contact Plaintr support
              </h5>
              <p>
                If you still want to request a refund, reach out to our support
                team:
              </p>
              <ul>
                <li>Log into your account and go to Support</li>
                <li>Select Request a Refund</li>
                <li>
                  Provide the details of your case and upload any relevant
                  documents
                </li>
              </ul>
              <p>This ensures your request is logged and tracked right away.</p>
            </div>
            <div>
              <h5 className="accordion-hfive">Step 3: Refund review</h5>
              <p>Once we receive your request:</p>
              <ul>
                <li>We’ll confirm that your payment is safely held</li>
                <li>
                  Our team will review your request and collect input from both
                  sides
                </li>
                <li>
                  If needed, the case will be escalated to the Internal
                  Resolution Panel (IRP) for a decision
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">
                Step 4: Outcome and resolution
              </h5>
              <p>You’ll receive:</p>
              <ul>
                <li>A written explanation of the decision</li>
                <li>Documentation of what was reviewed</li>
                <li>Confirmation of how funds will be released</li>
              </ul>
              <p>
                <strong>Timing:</strong> Most refund requests are resolved
                within 10 business days. Urgent cases (like filing deadlines)
                are prioritized within 3 business days.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">
                Your money is always protected
              </h5>
              <p>
                Throughout the process, your payment remains under a secure
                payment hold. No funds are released until the case is completed
                and reaches a clear outcome.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>What happens during a dispute on Plaintr</h4>
            <p>
              <em>
                Learn what to expect after opening a dispute on Plaintr — from
                review to resolution, with payments remaining securely held
                throughout the process.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">
                Disputes are rare — but we’re prepared
              </h5>
              <p>
                Most Plaintr cases go smoothly. But if a dispute is opened, we
                follow a clear, step-by-step process to make sure the outcome is
                fair for both clients and professionals.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Step 1: Initial review</h5>
              <p>
                Once your dispute is filed through the dashboard, our support
                team reviews the details and confirms that your payment remains
                under a secure payment hold.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Step 2: Information gathering</h5>
              <p>
                We ask both sides (you and your pro) to provide any relevant
                documents or messages. Only Plaintr-verified records — like
                platform messages, uploaded files, and payments — are used as
                evidence.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">
                Step 3: Internal Resolution Panel (IRP) review
              </h5>
              <p>
                If the issue isn’t resolved quickly, it’s escalated to our
                Internal Resolution Panel (IRP). This is a three-person team
                that includes a compliance officer and a senior CPA or EA. They
                review the evidence objectively and decide on the outcome.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Step 4: Decision and outcome</h5>
              <p>The IRP can decide on several outcomes, including:</p>
              <ul>
                <li>
                  <strong>Refund,</strong> full or partial, depending on the
                  case
                </li>
                <li>
                  <strong>Corrective work,</strong> the pro fixes the issue at
                  no extra cost
                </li>
                <li>
                  <strong>Reassignment,</strong> a new pro is assigned, if
                  appropriate
                </li>
                <li>
                  <strong>Release of payment,</strong> if the work was completed
                  as agreed
                </li>
              </ul>
              <p>
                You’ll always receive a written explanation of the decision.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Step 5: Appeal or closure</h5>
              <ul>
                <li>
                  If there’s new, material evidence, either side can appeal
                  within 5 business days.
                </li>
                <li>If not, the IRP decision stands.</li>
                <li>
                  <strong>Reassignment,</strong> a new pro is assigned, if
                  appropriate
                </li>
                <li>
                  In very rare cases, disputes may be escalated to binding
                  arbitration through the American Arbitration Association
                  (AAA).
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Timing you can expect</h5>
              <ul>
                <li>Standard cases: within 10 business days</li>
                <li>
                  Urgent cases (like filing deadlines): within 3 business days
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Your protection</h5>
              <p>
                At every stage, your payment remains under a secure payment
                hold. Funds are released only once the case is fully resolved.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>Who decides the outcome of a dispute on Plaintr</h4>
            <p>
              <em>
                Learn how Plaintr’s Internal Resolution Panel (IRP) reviews
                disputes and makes fair, consistent decisions.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">
                The role of the Internal Resolution Panel (IRP)
              </h5>
              <p>
                When a dispute can’t be resolved informally, it’s reviewed by
                Plaintr’s Internal Resolution Panel (IRP). This is the team
                responsible for making final decisions inside the platform.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">Who is on the IRP?</h5>
              <p>The panel includes:</p>
              <ul>
                <li>
                  A compliance officer to ensure decisions follow our policies
                  and industry standards.
                </li>
                <li>
                  A senior CPA or EA to review technical accuracy and
                  professional standards.
                </li>
                <li>
                  A Plaintr representative trained in dispute resolution and
                  client experience.
                </li>
              </ul>
              <p>
                This mix ensures every decision balances fairness, compliance,
                and tax expertise.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">How the IRP makes decisions</h5>
              <ul>
                <li>
                  The panel reviews only verified records from the Plaintr
                  platform (messages, documents, payments, deliverables).
                </li>
                <li>Both sides have the opportunity to submit evidence.</li>
                <li>
                  The panel applies the same rules to every case, so decisions
                  are consistent.
                </li>
                <li>
                  A written explanation of the outcome is shared with both
                  parties.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Are IRP decisions final?</h5>
              <ul>
                <li>In most cases, yes — IRP decisions are the final word.</li>
                <li>
                  Either side may appeal only if new, material evidence becomes
                  available. Appeals must be filed within 5 business days of the
                  IRP’s decision.
                </li>
                <li>
                  In very rare cases, disputes may be escalated further to
                  binding arbitration through the American Arbitration
                  Association (AAA).
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Why this matters</h5>
              <p>The IRP gives you the confidence that:</p>
              <ul>
                <li>Decisions aren’t made by a bot or a single employee.</li>
                <li>
                  Every case is reviewed by qualified professionals with tax
                  expertise.
                </li>
                <li>Outcomes are based on evidence, not opinion.</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Filing & Services",
      content: (
        <>
          <div className="general-accordion-content-block">
            <h4>Personal tax filing with Plaintr</h4>
            <p>
              <em>
                Plaintr makes personal tax filing simple by connecting you with
                certified experts and upfront pricing.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Filing, Plaintr style</h5>
              <p>
                Plaintr was built for people whose tax lives don’t fit into a
                single W-2. Whether you’re running a side hustle, trading
                crypto, managing real estate, or filing across multiple states,
                our Smart Match system connects you with the certified pro who
                knows how to handle your case.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">What’s covered</h5>
              <p>
                Plaintr supports a wide range of personal filing needs,
                including:
              </p>
              <ul>
                <li>Self-employment and 1099 income</li>
                <li>Side hustles and freelance work</li>
                <li>Stock trades, cryptocurrency, and other investments</li>
                <li>Real estate ownership and rental income</li>
                <li>Multi-state or complex filings</li>
                <li>
                  Common credits and deductions (for dependents, education,
                  etc.)
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Why it’s different</h5>
              <p>
                Every return is handled by a CPA, Enrolled Agent, or Tax
                Attorney — never a seasonal hire or uncertified preparer. With
                upfront pricing and a secure payment hold, you know your fee
                before you begin and your payment is released only after the
                work is complete.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>Business tax filing with Plaintr</h4>
            <p>
              <em>
                Plaintr connects LLCs, partnerships, and corporations with
                certified CPAs, EAs, and Tax Attorneys who specialize in complex
                business tax filings.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">
                Filing for your business, Plaintr style
              </h5>
              <p>
                Business taxes are rarely simple. From partnerships with
                multiple owners to S-corporations with payroll, compliance, and
                multi-state filings, Plaintr connects you with certified experts
                who know how to navigate the details.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">What’s covered</h5>
              <p>
                Our certified Pros handle a wide range of business filings,
                including:
              </p>
              <ul>
                <li>LLCs and multi-member partnerships</li>
                <li>S-corps and small corporations</li>
                <li>Complex deductions and credits</li>
                <li>Payroll, compliance, and reporting obligations</li>
                <li>
                  Multi-state operations or businesses with remote workers
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Why it’s different</h5>
              <p>
                With Plaintr, you don’t get routed to a retail office or a
                seasonal preparer. You’re matched with a CPA, EA, or Tax
                Attorney who specializes in business filings — and you’ll know
                your flat fee upfront, with your payment held securely until the
                work is complete.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Account & Security",
      content: (
        <>
          <div className="general-accordion-content-block">
            <h4>Managing your Plaintr account</h4>
            <p>
              <em>
                Learn how to manage your Plaintr account, update your
                information, and keep everything organized in one secure
                dashboard.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">
                Your Plaintr account, at your fingertips
              </h5>
              <p>
                Plaintr makes managing your account simple and secure. Here’s
                how you can keep your information up to date and control your
                preferences:
              </p>
              <ul>
                <li>
                  <strong>Log into your Dashboard</strong>{" "}
                  <ol>
                    <li>Sign in with your email and password.</li>
                  </ol>
                </li>
                <li>
                  <strong>Update your information</strong>{" "}
                  <ol>
                    <li>
                      Profile: Update your name, email, phone number, or
                      password.
                    </li>
                  </ol>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Why it matters</h5>
              <p>
                At Plaintr, we prioritize your security and convenience. Keeping
                your account updated ensures:
              </p>
              <ul>
                <li>
                  <strong>Seamless service — </strong>your pro gets the right
                  information for your case.
                </li>
                <li>
                  <strong>Peace of mind — </strong>your payment details are
                  securely stored, and you can track all transactions easily.
                </li>
              </ul>
              <p>
                Your account is your control center: secure, transparent, and
                personalized to your needs.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>Plaintr password, login, and recovery</h4>
            <p>
              <em>
                Learn how to log into your Plaintr account securely, recover
                your password, and protect your account with extra security
                measures.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Logging into your account</h5>
              <ul>
                <li>Enter your email and password to access your account.</li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Forgot your password?</h5>
              <p>No problem — just follow these steps:</p>
              <ul>
                <li>Click “Forgot password?” on the login page.</li>
                <li>Enter your registered email address.</li>
                <li>You’ll receive a secure link to reset your password.</li>
                <li>Create a new, strong password and log in.</li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Keeping your account secure</h5>
              <p>For added protection, we recommend:</p>
              <ul>
                <li>
                  Using unique, strong passwords for your Plaintr account.
                </li>
                <li>
                  Avoiding shared access — don’t share your login credentials
                  with anyone.
                </li>
              </ul>
              <p>
                Plaintr protects your information through secure, encrypted
                connections. All payments and financial transactions are
                processed by our trusted partner, Stripe — a PCI Level 1
                certified provider that uses bank-level encryption to keep your
                financial data safe.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>How Plaintr Protects Your Data</h4>
            <p>
              <em>
                Learn how Plaintr uses encryption and other security measures to
                protect your personal data and ensure your privacy.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">Security you can trust</h5>
              <p>
                Plaintr is committed to keeping your data safe. We use
                industry-standard encryption and secure servers to protect your
                personal information.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">How we protect you</h5>
              <ul>
                <li>
                  <strong>Encryption:</strong> We protect your information
                  through secure, encrypted connections. All payments and
                  financial transactions are processed by our trusted partner,
                  Stripe — a PCI Level 1 certified provider that uses bank-level
                  encryption to keep your financial data safe.
                </li>
                <li>
                  <strong>Access control:</strong> Only authorized Plaintr team
                  members have access to your data, and we use secure methods
                  for any internal processes that require it.
                </li>
                <li>
                  <strong>Compliance:</strong> Plaintr complies with all
                  relevant privacy laws and regulations, including GDPR and IRS
                  Circular 230.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Your privacy matters</h5>
              <p>
                Plaintr never sells your data to third parties, and we don’t use
                your information for anything other than providing you with the
                best tax service.
              </p>
              <p>
                You’re in control — you can always review, update, or delete
                your personal data through your account settings.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Support & Contact",
      content: (
        <>
          <div className="general-accordion-content-block">
            <h4>How Support Works at Plaintr</h4>
            <p>
              <em>
                Learn how to reach out to Plaintr support and what to expect
                when you do.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">How Support Works at Plaintr</h5>
              <p>
                Plaintr offers support exclusively for registered users —
                whether you're a client or a pro. We provide personalized
                support to help you navigate the platform and resolve any
                issues, but support is not available to the general public or to
                individuals who are not registered on the platform.
              </p>
            </div>
            <div>
              <h5 className="accordion-hfive">
                Support channels for registered users
              </h5>
              <ul>
                <li>
                  <strong>Support integrated in the dashboard:</strong> Once
                  you're registered, you’ll have access to a dedicated support
                  section in your dashboard. This is where you can reach out for
                  help.
                </li>
                <li>
                  <strong>Email support:</strong> You can also email us at
                  support@plaintr.com for assistance.
                </li>
                <li>
                  <strong>Response times:</strong>
                  <ol>
                    <li>
                      <strong>Dashboard support:</strong> Expect a response
                      within 1 business day.
                    </li>
                    <li>
                      <strong>Email:</strong> We aim to respond within 1
                      business day as well.
                    </li>
                  </ol>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Escalation process</h5>
              <p>
                If your issue requires more attention, it will be escalated as
                follows:
              </p>
              <ul>
                <li>
                  <strong>Initial support:</strong> The support team will try to
                  resolve your issue promptly.
                </li>
                <li>
                  <strong>Specialized support:</strong> If needed, your issue
                  will be escalated to a specialized team.
                </li>
                <li>
                  <strong>Management escalation:</strong> In rare cases, if the
                  issue isn’t resolved, it will be escalated to the Plaintr
                  management team.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Why it matters</h5>
              <p>
                By offering exclusive support to registered users, Plaintr
                ensures that we’re able to provide the best possible service to
                people who are actively engaged with the platform.
                Non-registered users can access self-service resources like the
                Help Center and Frequently Asked Questions, but we prioritize
                support for users committed to working with us.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>Contact Us</h4>
          </div>
          <div className="general-accordion-content-block">
            <h4>Status & Incident Updates</h4>
            <p>
              <em>
                Learn how to reach out to Plaintr if you need assistance or have
                questions.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">What you need to know</h5>
              <p>
                We’re here for our registered users. If you have a client or pro
                account, here’s how to get in touch with us:
              </p>
              <ul>
                <li>
                  <strong>Email:</strong> For general inquiries, send an email
                  to contact@plaintr.com.
                </li>
                <li>
                  <strong>Support through the dashboard:</strong> For specific
                  issues related to your account, please use the support feature
                  directly available in your dashboard.
                </li>
              </ul>
            </div>
            <div>
              <h5 className="accordion-hfive">Why it matters</h5>
              <p>
                Plaintr is committed to providing support and clear
                communication for registered users. We offer multiple ways to
                get in touch to ensure that you can always reach us when you
                need help with the platform.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Policies & Legal",
      content: (
        <>
          <div className="general-accordion-content-block">
            <h4>Understanding Plaintr’s Terms & Conditions</h4>
            <p>
              <em>
                Learn the basics of how Plaintr works and the rules you agree to
                when using the platform.
              </em>
            </p>
            <div>
              <h5 className="accordion-hfive">What you need to know</h5>
              <p>
                Plaintr’s Terms & Conditions set the guidelines for how everyone
                — whether you’re a client or a pro — uses the platform. These
                rules are here to help keep things fair and ensure that everyone
                has a smooth, positive experience.
              </p>
              <p>Here’s what’s covered:</p>
              <ul>
                <li>
                  <strong>What Plaintr does:</strong> Plaintr connects clients
                  with certified tax professionals (CPAs, EAs, and Tax
                  Attorneys). We don’t provide tax services ourselves — but we
                  make sure clients are matched with the right pro for their
                  needs.
                </li>
                <li>
                  <strong>Your role:</strong> As a client, you’ll provide the
                  necessary information and pay for the services you need. As a
                  pro, you’re an independent contractor who delivers the
                  agreed-upon services to clients.
                </li>
                <li>
                  <strong>Payments:</strong> Clients pay upfront, and pros are
                  paid through Plaintr after the work is completed, with funds
                  securely held until the job is done.
                </li>
                <li>
                  <strong>Confidentiality:</strong> Plaintr ensures that both
                  clients' and pros' data is kept private and secure.
                </li>
                <li>
                  <strong>Dispute resolution:</strong> If something goes wrong,
                  we have a process to help both sides resolve issues fairly.
                </li>
                <li>
                  <strong>Indemnification:</strong> Pros are responsible for the
                  services they provide, but Plaintr’s platform supports them
                  with tools and guidelines. Clients also need to be honest and
                  transparent in the information they provide.
                </li>
              </ul>
            </div>
            <div>
              <h5>Why it matters</h5>
              <p>
                By using Plaintr, both clients and pros agree to follow these
                guidelines, ensuring a safe, fair, and transparent experience
                for everyone. These terms protect both parties and outline how
                we work together to ensure that things run smoothly. You can
                always refer back to the full Terms & Conditions for all the
                details.
              </p>
            </div>
          </div>
          <div className="general-accordion-content-block">
            <h4>Refund Policy</h4>
            <p>
              <em>
                Learn when you may be eligible for a refund and how the process
                works.
              </em>
            </p>
          </div>
        </>
      ),
    },
    {
      title: "Dispute Resolution",
      content: (
        <>
          <div className="general-accordion-content-block">
            <p>
              <em>
                Learn the basics of how Plaintr works and the rules you agree to
                when using the platform.
              </em>
            </p>
          </div>
          <div className="general-accordion-content-block">
            <h4>What you need to know</h4>
            <p>
              Plaintr has a simple, transparent dispute resolution process
              designed to help clients and pros resolve issues quickly and
              fairly.
            </p>
          </div>
          <div className="general-accordion-content-block">
            <h4>How disputes are handled</h4>
            <ul>
              <li>
                <strong>Filing a dispute:</strong> Either a client or a pro can
                file a dispute using the Plaintr dashboard or by emailing
                support@plaintr.com
              </li>
              <li>
                <strong>Submitting evidence:</strong> Both parties must submit
                evidence within 3 business days of filing the dispute. This
                could include communication logs, documents, or any other
                relevant materials.
              </li>
              <li>
                <strong>Review by IRP:</strong> The Internal Resolution Panel
                (IRP) will review the dispute and aim to make a decision within
                10 business days (or 3 business days for urgent cases).
              </li>
              <li>
                <strong>Possible outcomes:</strong>
                <ol>
                  <li>
                    <strong>Full refund</strong> if no work was performed or
                    services were not delivered properly.
                  </li>
                  <li>
                    <strong>Partial refund</strong> if services were incomplete.
                  </li>
                  <li>
                    <strong>Corrective</strong> work if the pro needs to fix an
                    issue at no extra cost.
                  </li>
                  <li>
                    <strong>Reassignment</strong> if the pro isn’t a good fit
                    for the client’s needs.
                  </li>
                  <li>
                    <strong>Full payment release</strong> if the work was
                    completed as agreed.
                  </li>
                </ol>
              </li>
            </ul>
          </div>
          <div className="general-accordion-content-block">
            <h4>Escalating to arbitration</h4>
            <p>
              If a party is unsatisfied with the IRP’s decision, the dispute can
              be taken to binding arbitration with the American Arbitration
              Association (AAA). This step is final and enforceable.
            </p>
          </div>
          <div className="general-accordion-content-block">
            <h4>Escalating to arbitration</h4>
            <p>
              If a party is unsatisfied with the IRP’s decision, the dispute can
              be taken to binding arbitration with the American Arbitration
              Association (AAA). This step is final and enforceable.
            </p>
          </div>
          <div className="general-accordion-content-block">
            <h4>Why it matters</h4>
            <p>
              Plaintr’s Dispute Resolution Policy is designed to be fair and
              transparent. It ensures that any issue gets addressed and resolved
              quickly, with clear steps and a clear timeline, so you don’t have
              to worry about delays or uncertainty.
            </p>
          </div>
        </>
      ),
    },
    {
      title: "Privacy & compliance basics",
      content: (
        <>
          <p>
            <em>
              Learn how Plaintr protects your information and ensures compliance
              with laws.
            </em>
          </p>
          <div className="general-accordion-content-block">
            <h4>What you need to know</h4>
            <p>
              Plaintr takes data privacy and compliance seriously. Here’s how we
              protect your information and stay in line with the law:
            </p>
          </div>
          <div className="general-accordion-content-block">
            <h4>How Plaintr protects your privacy</h4>
            <ul>
              <li>
                <strong>Secure data:</strong> We use industry-standard security
                measures to keep your personal and professional information
                safe.
              </li>
              <li>
                <strong>Data collection:</strong> Plaintr only collects the
                necessary information to connect you with clients — like your
                name, credentials, contact info, and the services you provide.
              </li>
              <li>
                <strong>Who can access your data:</strong> Only Plaintr,
                clients, and pros involved in the engagement can access the
                relevant information.
              </li>
            </ul>
          </div>
          <div className="general-accordion-content-block">
            <h4>How we stay compliant</h4>
            <ul>
              <li>
                <strong>IRS and state compliance:</strong> We comply with IRS
                Circular 230 and state laws to make sure we follow tax industry
                regulations.
              </li>
              <li>
                <strong>FTC rules:</strong> Plaintr follows Federal Trade
                Commission (FTC) rules to ensure fair and transparent service
                for all users.
              </li>
              <li>
                <strong>State-specific laws:</strong> We respect state consumer
                protection laws in areas like California, New York, and Florida,
                ensuring compliance on a local level.
              </li>
            </ul>
          </div>
          <div className="general-accordion-content-block">
            <h4>Why it matters</h4>
            <p>
              When you use Plaintr, your data is safe and we follow all the
              necessary laws. This transparency builds trust, so both clients
              and pros feel confident that their information is protected.
            </p>
          </div>
        </>
      ),
    },
  ],
};
